import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { municipios_pe_adepe } from "../../global/layerStyles/municipios_pe_adepe";
import { parseVocacoes, Vocacao } from "../../utils/vocacoes";

const MATURIDADE_HINT =
  "Classificação da ADEPE sobre o quanto a vocação está estabelecida no município.";

interface VocacaoDetailModalProps {
  /** Feição do município clicado; `null` mantém o modal fechado. */
  feature: any | null;
  onClose: () => void;
}

function Badge({ children, title }: { children: string; title?: string }) {
  return (
    <span
      title={title}
      className="inline-flex items-center rounded-full border border-[#d4d4d8] bg-[#f4f4f5] px-2 py-0.5 text-xs text-[#3f3f46]"
    >
      {children}
    </span>
  );
}

function VocacaoItem({ vocacao }: { vocacao: Vocacao }) {
  const { rank, nome, maturidade, porte, produtos, especializacao } = vocacao;

  return (
    <li className="flex gap-3 border-b border-[#e4e4e7] py-3 last:border-b-0">
      <span className="w-5 shrink-0 text-right font-semibold text-[#71717a]">
        {rank}.
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={rank === 1 ? "font-semibold" : "font-medium"}>
            {nome}
          </span>
          {maturidade && <Badge title={MATURIDADE_HINT}>{maturidade}</Badge>}
          {porte && <Badge>{`Porte ${porte.toLowerCase()}`}</Badge>}
        </div>
        {produtos && (
          <span className="text-sm text-[#52525b]">
            Principais produtos: {produtos}
          </span>
        )}
        {especializacao && (
          <span className="text-sm text-[#52525b]">
            Especialização: {especializacao}
          </span>
        )}
      </div>
    </li>
  );
}

/**
 * Detalhe das vocações de um município, aberto pelo clique na camada
 * "Vocações Econômicas". O hover mostra só os nomes; aqui entram maturidade,
 * porte, produtos e especialização de cada uma.
 */
export function VocacaoDetailModal({
  feature,
  onClose,
}: VocacaoDetailModalProps) {
  if (!feature) return null;

  const properties = feature.properties ?? {};
  const vocacoes = parseVocacoes(properties);
  const color = municipios_pe_adepe(feature).fillColor;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {color && (
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-sm border border-[#404040]"
                style={{ backgroundColor: color }}
              />
            )}
            {properties.nm_mun ?? "Município"}
          </DialogTitle>
          {properties.rd && (
            <DialogDescription>
              Região de Desenvolvimento: {properties.rd}
            </DialogDescription>
          )}
        </DialogHeader>

        <ol>
          {vocacoes.map((vocacao) => (
            <VocacaoItem key={vocacao.rank} vocacao={vocacao} />
          ))}
        </ol>

        <p className="text-xs text-[#777]">
          Vocações na ordem classificada pela ADEPE. Maturidade e porte conforme
          classificação da ADEPE.
        </p>
      </DialogContent>
    </Dialog>
  );
}
